/**
 * The one decision a focus trap makes, as a pure function.
 *
 * Extracted 2026-09-05 because the trap it serves is keyboard logic that
 * NOTHING could test: component tests are deliberately out of this repo's
 * harness (`vitest.config.ts` is node-only, no jsdom — see CLAUDE.md), so the
 * wrap behaviour was verified once by hand in a browser and then pinned by
 * nothing. The DOM half stays in the component; the arithmetic lives here,
 * where the existing suite reaches it.
 *
 * ⚠️ `aria-modal="true"` does NOT trap keyboard focus — it only tells assistive
 * tech the rest of the page is inert. Measured on the mobile menu before this
 * existed: 10 tabbables inside the dialog, 111 still reachable outside it. A
 * dialog that claims modality has to deliver it.
 */

/**
 * Which item Tab should land on, or `null` to let the browser move focus
 * normally (the common case — most tabs are interior moves the trap must not
 * touch, or the trap would fight the browser on every keystroke).
 *
 * @param count       how many focusable items the container holds
 * @param activeIndex index of the focused item, or -1 when focus is OUTSIDE
 * @param shiftKey    true for Shift+Tab
 */
export function nextTrapIndex(count: number, activeIndex: number, shiftKey: boolean): number | null {
  if (count <= 0) return null;
  const last = count - 1;
  // Focus has escaped (or never entered) — pull it back to whichever end the
  // reader is travelling towards, so the trap is self-healing rather than only
  // preventing the exit it happens to witness.
  if (activeIndex < 0) return shiftKey ? last : 0;
  if (shiftKey && activeIndex === 0) return last;
  if (!shiftKey && activeIndex === last) return 0;
  return null;
}
